"use client"

import { motion, AnimatePresence } from "framer-motion"
import { useTranslations } from "next-intl"
import { useCharacterStore } from "@/features/characters/store"
import { cn } from "@/shared/lib/utils"
import { Icon } from "@/shared/components/icons"
import { STYLES } from "./appearance-step"
import { STYLE_PREVIEW_VIDEOS } from "@/features/create/lib/wizard-preview-videos"
import { WizardPreviewVideo } from "./wizard-preview-video"

export function BasicsStep({ invalidFields = [] }: { invalidFields?: string[] }) {
  const t = useTranslations("create.wizard")
  const { draft, updateDraft } = useCharacterStore()
  const hasSelection = !!draft.style
  const isStyleInvalid = invalidFields.includes("style")

  return (
    <div className="space-y-4">
      {/* Style — two large cards */}
      <div
        className={cn(
          "grid grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto rounded-[6px] transition-all p-1",
          isStyleInvalid && "ring-1 ring-red-500/40 border border-red-500/30 bg-red-500/[0.04]"
        )}
      >
        {STYLES.map(({ value }) => {
          const styleLabel =
            value === "anime" ? t("styles.anime.label") : t("styles.realistic.label")
          const videoSources = STYLE_PREVIEW_VIDEOS[value]
          const isSelected = draft.style === value
          const isUnselectedOther = hasSelection && !isSelected
          return (
            <motion.button
              key={value}
              type="button"
              onClick={() => updateDraft({ style: value })}
              whileTap={{ scale: 0.97 }}
              animate={{ scale: isSelected ? 1.02 : 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 26 }}
              className={cn(
                "group relative isolate overflow-hidden rounded-[6px] border-2 transition-[opacity,border-color,box-shadow] duration-300 ease-out aspect-[3/4] max-h-[52dvh]",
                "opacity-80",
                hasSelection && isSelected && "opacity-100",
                isUnselectedOther && "opacity-45 hover:opacity-55",
                !hasSelection && "hover:opacity-100",
                isSelected
                  ? "border-accent-blue/50 ring-2 ring-accent-blue/30"
                  : "border-border/50 hover:border-white/30"
              )}
            >
              <WizardPreviewVideo
                mp4={videoSources.mp4}
                poster={videoSources.poster}
                label={styleLabel}
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                <span className="block text-lg font-bold text-white">
                  {styleLabel}
                </span>
              </div>
              <AnimatePresence>
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 500, damping: 20 }}
                    className="absolute top-3 right-3 flex size-7 items-center justify-center rounded-full bg-accent-blue"
                  >
                    <svg
                      className="size-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <motion.path
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.25, ease: "easeOut", delay: 0.08 }}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          )
        })}
      </div>
      {isStyleInvalid && (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-red-500/20 bg-red-500/[0.06] px-3.5 py-2.5">
          <Icon name="attention-circle" size={16} className="size-4 shrink-0 text-red-400" />
          <p className="text-sm text-red-400">{t("styleRequired")}</p>
        </div>
      )}
    </div>
  )
}
